import { safeZodPath } from "@impero-com/common/utils/form/safeZodPath";
import { assertNever } from "@impero-com/common/utils/obj";
import { isEqual } from "lodash-es";
import { z } from "zod";
import { ParsePayload } from "zod/v4/core";

import { Formula, RiskDirectoryBlessedFieldIds } from "api/generated/model";

import { allVariables, parseFormula } from "../formulaParser";

import { SectionLevel, sectionSchema } from "./sections";

const internalFormSchema = z.object({
    sections: z.array(sectionSchema),
});
type IFormSchema = z.infer<typeof internalFormSchema>;

export const formSchema = (blessedFieldIds: RiskDirectoryBlessedFieldIds) =>
    internalFormSchema
        .check((ctx) => validateSectionsLevel(ctx.value, ctx))
        .check((ctx) => validateFieldsNameUniqueness(ctx.value, ctx))
        .check((ctx) => validateConditions(ctx.value, ctx))
        .check((ctx) => validateFormula(ctx.value, ctx))
        .check((ctx) =>
            validateInherentAndResidualOptions(
                ctx.value,
                ctx,
                blessedFieldIds
            )()
        );
export type FormSchema = z.infer<ReturnType<typeof formSchema>>;

interface FieldPath {
    sectionIndex: number;
    subsectionIndex: number;
    fieldIndex: number;
}

function validateSectionsLevel(
    val: IFormSchema,
    ctx: ParsePayload<Record<string, unknown>>
) {
    let current: SectionLevel = "RiskDirectory";
    val.sections.forEach((section, sectionIndex) => {
        switch (section.level) {
            case "RiskDirectory": {
                if (current !== "RiskDirectory") {
                    ctx.issues.push({
                        code: "custom",
                        path: safeZodPath<IFormSchema>(
                            `sections.${sectionIndex}.level`
                        ),
                        message:
                            "Risk directory level fields must appear before entity level fields",
                        input: ctx.value,
                    });
                }
                break;
            }
            case "Entity": {
                current = "Entity";
                break;
            }
            default: {
                return assertNever(section.level);
            }
        }
    });
}

function validateFieldsNameUniqueness(val: IFormSchema, ctx: ParsePayload) {
    const sectionLevelToFieldNameToFieldPath: Map<
        SectionLevel,
        Map<string, Array<FieldPath>>
    > = new Map();
    val.sections.forEach((section, sectionIndex) =>
        section.subsections.forEach((subs, subsectionIndex) =>
            subs.fields.forEach((field, fieldIndex) => {
                if (field.fieldType.type === "Heading") {
                    return;
                }
                const fieldPath: FieldPath = {
                    fieldIndex,
                    sectionIndex,
                    subsectionIndex,
                };
                if (!sectionLevelToFieldNameToFieldPath.has(section.level)) {
                    sectionLevelToFieldNameToFieldPath.set(
                        section.level,
                        new Map()
                    );
                }
                // SAFETY: We have just made sure above that there is a map for this section.
                const fieldNameToFieldPaths =
                    sectionLevelToFieldNameToFieldPath.get(section.level)!;
                // Use lowercase for field names because the check is case-insensitive
                const fieldNameLowercased = field.name.toLowerCase();
                if (fieldNameToFieldPaths.has(fieldNameLowercased)) {
                    const newEntry: Array<FieldPath> = [
                        ...fieldNameToFieldPaths.get(fieldNameLowercased)!,
                        fieldPath,
                    ];
                    fieldNameToFieldPaths.set(fieldNameLowercased, newEntry);
                } else {
                    fieldNameToFieldPaths.set(fieldNameLowercased, [fieldPath]);
                }
            })
        )
    );

    sectionLevelToFieldNameToFieldPath.forEach(
        (fieldNameToFieldPath, sectionLevel) => {
            fieldNameToFieldPath.forEach((fieldPaths, fieldName) => {
                // More than 1 fields corresponding to the same case-insensitive field name
                // => raise issues for all fields, saying that the name is not unique.
                if (fieldPaths.length > 1) {
                    fieldPaths.forEach((fPath) => {
                        ctx.issues.push({
                            code: "custom",
                            path: safeZodPath<IFormSchema>(
                                `sections.${fPath.sectionIndex}.subsections.${fPath.subsectionIndex}.fields.${fPath.fieldIndex}.name`
                            ),
                            message:
                                "The field name " +
                                fieldName +
                                " is not unique (case insensitive) for fields at the " +
                                sectionLevel +
                                " level",
                            input: ctx.value,
                        });
                    });
                }
            });
        }
    );
}

function validateConditions(
    val: IFormSchema,
    ctx: ParsePayload<Record<string, unknown>>
) {
    const fieldIdsToOptionValues: Map<string, Set<string>> = new Map();
    val.sections.forEach((section, sectionIndex) =>
        section.subsections.forEach((subs, subsectionIndex) =>
            subs.fields.forEach((field, fieldIndex) => {
                const fieldPath: FieldPath = {
                    fieldIndex,
                    sectionIndex,
                    subsectionIndex,
                };
                if (field.condition !== null) {
                    if (!fieldIdsToOptionValues.has(field.condition.columnId)) {
                        // Condition on a field that does not exist before this field
                        ctx.issues.push({
                            code: "custom",
                            path: safeZodPath<IFormSchema>(
                                `sections.${fieldPath.sectionIndex}.subsections.${fieldPath.subsectionIndex}.fields.${fieldPath.fieldIndex}.condition`
                            ),
                            message:
                                "The field referenced by this condition is not found before",
                            input: ctx.value,
                        });
                    } else {
                        // SAFETY: asserted above that the field exists
                        const conditionOptions = fieldIdsToOptionValues.get(
                            field.condition.columnId
                        )!;
                        if (!conditionOptions.has(field.condition.value)) {
                            // Condition on an option that does not exist in this field
                            ctx.issues.push({
                                code: "custom",
                                path: safeZodPath<IFormSchema>(
                                    `sections.${fieldPath.sectionIndex}.subsections.${fieldPath.subsectionIndex}.fields.${fieldPath.fieldIndex}.condition`
                                ),
                                message:
                                    "The option referenced by this condition is not found in the field",
                                input: ctx.value,
                            });
                        }
                    }
                }
                const optionIdsSet: Set<string> = new Set();
                switch (field.fieldType.type) {
                    case "Heading":
                    case "TextArea":
                    case "TextInput":
                    case "NumericInput":
                    case "CheckboxGroup":
                    case "AttachedFiles":
                        break;
                    case "RadioButtonGroup":
                    case "Dropdown":
                        field.fieldType.options.forEach((opt) => {
                            optionIdsSet.add(opt.optionId);
                        });
                        break;
                    default:
                        assertNever(field.fieldType);
                }
                fieldIdsToOptionValues.set(field.fieldId, optionIdsSet);
            })
        )
    );
}

function validateFormula(
    val: IFormSchema,
    ctx: ParsePayload<Record<string, unknown>>
) {
    const fieldIdsAllowedInFormula: Map<string, boolean> = new Map();
    val.sections.forEach((section, sectionIndex) =>
        section.subsections.forEach((subs, subsectionIndex) =>
            subs.fields.forEach((field, fieldIndex) => {
                const fieldPath: FieldPath = {
                    fieldIndex,
                    sectionIndex,
                    subsectionIndex,
                };
                let formula: Formula | null;
                switch (field.fieldType.type) {
                    case "Heading":
                    case "TextArea":
                    case "TextInput":
                    case "CheckboxGroup":
                    case "AttachedFiles":
                        formula = null;
                        break;
                    case "NumericInput":
                    case "RadioButtonGroup":
                    case "Dropdown":
                        formula = field.fieldType.formula;
                        break;
                    default:
                        assertNever(field.fieldType);
                }
                if (formula !== null) {
                    const parseResult = parseFormula(formula);
                    if (parseResult.isOk) {
                        const expr = parseResult.value;
                        const variables = allVariables(expr);
                        for (const variable of variables) {
                            const allowed = fieldIdsAllowedInFormula.get(
                                variable.uuid
                            );
                            if (allowed === undefined) {
                                ctx.issues.push({
                                    code: "custom",
                                    path: safeZodPath<IFormSchema>(
                                        `sections.${fieldPath.sectionIndex}.subsections.${fieldPath.subsectionIndex}.fields.${fieldPath.fieldIndex}.fieldType.formula`
                                    ),
                                    message: `Field \$${variable.uuid} referenced by this formula is not found before`,
                                    input: ctx.value,
                                });
                            } else if (!allowed) {
                                ctx.issues.push({
                                    code: "custom",
                                    path: safeZodPath<IFormSchema>(
                                        `sections.${fieldPath.sectionIndex}.subsections.${fieldPath.subsectionIndex}.fields.${fieldPath.fieldIndex}.fieldType.formula`
                                    ),
                                    message: `Field \$${variable.uuid} cannot be referenced by this formula because it has invalid type`,
                                    input: ctx.value,
                                });
                            }
                        }
                    }
                }
                switch (field.fieldType.type) {
                    case "Heading":
                    case "TextArea":
                    case "TextInput":
                    case "CheckboxGroup":
                    case "AttachedFiles":
                        fieldIdsAllowedInFormula.set(field.fieldId, false);
                        break;
                    case "NumericInput":
                    case "RadioButtonGroup":
                    case "Dropdown":
                        fieldIdsAllowedInFormula.set(field.fieldId, true);
                        break;
                    default:
                        assertNever(field.fieldType);
                }
            })
        )
    );
}

function validateInherentAndResidualOptions(
    val: IFormSchema,
    ctx: ParsePayload,
    blessedFieldIds: RiskDirectoryBlessedFieldIds
) {
    return () => {
        const blessedFieldIdsToOptionValues: Map<
            string,
            [FieldPath, Array<string>]
        > = new Map();
        val.sections.forEach((section, sectionIndex) => {
            section.subsections.forEach((subs, subsectionIndex) => {
                subs.fields.forEach((field, fieldIndex) => {
                    switch (field.fieldId) {
                        case blessedFieldIds.inherentImpactId:
                        case blessedFieldIds.inherentLikelihoodId:
                        case blessedFieldIds.residualImpactId:
                        case blessedFieldIds.residualLikelihoodId: {
                            let options: Array<string>;
                            switch (field.fieldType.type) {
                                case "Heading":
                                case "TextArea":
                                case "TextInput":
                                case "AttachedFiles":
                                case "NumericInput":
                                case "CheckboxGroup": {
                                    ctx.issues.push({
                                        code: "custom",
                                        path: safeZodPath<IFormSchema>(
                                            `sections.${sectionIndex}.subsections.${subsectionIndex}.fields.${fieldIndex}.fieldType`
                                        ),
                                        message:
                                            "Field type should be a single option (dropdown or radio)",
                                        input: ctx.value,
                                    });
                                    options = [];
                                    break;
                                }
                                case "RadioButtonGroup":
                                case "Dropdown": {
                                    options = field.fieldType.options.map(
                                        (opt) => opt.value
                                    );
                                    break;
                                }
                                default: {
                                    return assertNever(field.fieldType);
                                }
                            }
                            blessedFieldIdsToOptionValues.set(field.fieldId, [
                                {
                                    sectionIndex,
                                    subsectionIndex,
                                    fieldIndex,
                                },
                                options,
                            ]);
                            break;
                        }
                        case blessedFieldIds.responseId: {
                            if (field.fieldType.type !== "Dropdown") {
                                ctx.issues.push({
                                    code: "custom",
                                    path: safeZodPath<IFormSchema>(
                                        `sections.${sectionIndex}.subsections.${subsectionIndex}.fields.${fieldIndex}.fieldType`
                                    ),
                                    message:
                                        "Response field should be a dropdown",
                                    input: ctx.value,
                                });
                            }
                            break;
                        }
                    }
                });
            });
        });
        if (blessedFieldIdsToOptionValues.size !== 4) {
            throw new Error("Need 4 blessed fields");
        }
        for (const [inherentFieldId, residualFieldId, description] of [
            [
                blessedFieldIds.inherentImpactId,
                blessedFieldIds.residualImpactId,
                "impact",
            ],
            [
                blessedFieldIds.inherentLikelihoodId,
                blessedFieldIds.residualLikelihoodId,
                "likelihood",
            ],
        ]) {
            const inherent = blessedFieldIdsToOptionValues.get(inherentFieldId);
            if (!inherent) {
                throw new Error(`Did not find inherent ${description} field`);
            }
            const [inherentPath, inherentOptions] = inherent;

            const residual = blessedFieldIdsToOptionValues.get(residualFieldId);
            if (!residual) {
                throw new Error(`Did not find residual ${description} field`);
            }
            const [residualPath, residualOptions] = residual;

            if (!isEqual(inherentOptions, residualOptions)) {
                const inherentOptionsSet = new Set(inherentOptions);
                const residualOptionsSet = new Set(residualOptions);
                const message = !isEqual(inherentOptionsSet, residualOptionsSet)
                    ? `The inherent and residual ${description} fields have different options`
                    : `The inherent and residual ${description} fields have the same options but in a different order`;
                for (const fPath of [inherentPath, residualPath]) {
                    ctx.issues.push({
                        code: "custom",
                        path: safeZodPath<IFormSchema>(
                            `sections.${fPath.sectionIndex}.subsections.${fPath.subsectionIndex}.fields.${fPath.fieldIndex}.fieldType.options`
                        ),
                        message,
                        input: ctx.value,
                    });
                }
            }
        }
    };
}
